import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getBusiness, updateBusiness, uploadFacturamaCertificate, uploadFacturamaPrivateKey, getCSDStatus, registrarCSD, diagnosticarCSD } from '../../services/api';
import api from '../../services/api';
import { Settings, CreditCard, Truck, CheckCircle, MapPin, FileText, Award, Upload, File, X, Loader2, Image, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import ImageUpload from '../../components/ImageUpload';

const Configuracion = () => {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [csdStatus, setCsdStatus] = useState(null);
  const [registrandoCSD, setRegistrandoCSD] = useState(false);
  const [certFileName, setCertFileName] = useState('');
  const [keyFileName, setKeyFileName] = useState('');
  const [logoFileName, setLogoFileName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    rfc: '',
    phone: '',
    email: '',
    address: '',
    postal_code: '',
    logo_url: '',
    logo_thumbnail_url: '',
    lat: '',
    lng: '',
    // Facturama multiemisor fields
    facturama_rfc: '',
    facturama_razon_social: '',
    facturama_codigo_postal: '',
    regimen_fiscal: '601',
    facturama_logo: '',
    // CSD files - transient, only kept in local state for registration
    facturama_certificate: '',
    facturama_private_key: '',
    facturama_private_key_password: '',
    settings: {
      delivery_enabled: false,
      pickup_enabled: true,
      shipping_enabled: false,
      payment_methods: [],
      verification_badge: false,
      billing_enabled: false,
      wholesale_enabled: false,
      wholesale_conditions: '',
    },
  });

  useEffect(() => {
    loadBusiness();
  }, [user]);

  const loadBusiness = async () => {
    if (!user?.business_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await getBusiness(user.business_id);
      const businessData = response.data;
      setBusiness(businessData);

      // Load CSD status if billing is enabled
      if (businessData.settings?.billing_enabled) {
        try {
          const csdRes = await getCSDStatus();
          setCsdStatus(csdRes.data);
        } catch (e) {
          console.error('Error loading CSD status:', e);
        }
      }

      setFormData({
        name: businessData.name || '',
        rfc: businessData.rfc || '',
        phone: businessData.phone || '',
        email: businessData.email || '',
        address: businessData.address || '',
        postal_code: businessData.location?.postal_code || '',
        logo_url: businessData.logo_url || '',
        logo_thumbnail_url: businessData.logo_thumbnail_url || '',
        lat: businessData.location?.lat || '',
        lng: businessData.location?.lng || '',
        facturama_rfc: businessData.facturama_rfc || '',
        facturama_razon_social: businessData.facturama_razon_social || '',
        facturama_codigo_postal: businessData.facturama_codigo_postal || '',
        regimen_fiscal: businessData.regimen_fiscal || '601',
        facturama_logo: businessData.facturama_logo || '',
        // CSD files start empty - user uploads each time they need to register
        facturama_certificate: '',
        facturama_private_key: '',
        facturama_private_key_password: '',
        settings: businessData.settings || {
          delivery_enabled: false,
          pickup_enabled: true,
          shipping_enabled: false,
          payment_methods: [],
          verification_badge: false,
          billing_enabled: false,
          wholesale_enabled: false,
          wholesale_conditions: '',
        },
      });
    } catch (error) {
      console.error('Error loading business:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        rfc: formData.rfc,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        logo_url: formData.logo_url,
        logo_thumbnail_url: formData.logo_thumbnail_url,
        location: {
          lat: formData.lat ? parseFloat(formData.lat) : null,
          lng: formData.lng ? parseFloat(formData.lng) : null,
          postal_code: formData.postal_code || null,
        },
        settings: formData.settings,
      };

      // Only include Facturama config if billing is enabled (NO cert/key/password - those are transient)
      if (formData.settings.billing_enabled) {
        updateData.facturama_rfc = formData.facturama_rfc;
        updateData.facturama_razon_social = formData.facturama_razon_social;
        updateData.facturama_codigo_postal = formData.facturama_codigo_postal;
        updateData.regimen_fiscal = formData.regimen_fiscal;
        updateData.facturama_logo = formData.facturama_logo;
      }

      await updateBusiness(user.business_id, updateData);
      toast.success('Configuración guardada exitosamente');
      loadBusiness();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(getErrorMessage(error, 'Error al guardar configuración'));
    } finally {
      setSaving(false);
    }
  };

  const togglePaymentMethod = (method) => {
    const newMethods = formData.settings.payment_methods.includes(method)
      ? formData.settings.payment_methods.filter((m) => m !== method)
      : [...formData.settings.payment_methods, method];
    setFormData({ 
      ...formData, 
      settings: { ...formData.settings, payment_methods: newMethods } 
    });
  };

  const updateSetting = (key, value) => {
    setFormData({
      ...formData,
      settings: { ...formData.settings, [key]: value }
    });
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.cer')) {
      toast.error('El archivo debe tener extensión .cer');
      return;
    }

    setUploadingCert(true);
    try {
      const response = await uploadFacturamaCertificate(file);
      setFormData({ ...formData, facturama_certificate: response.data.certificate_base64 });
      setCertFileName(file.name);
      toast.success('Certificado subido exitosamente');
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error(getErrorMessage(error, 'Error al subir el certificado'));
    } finally {
      setUploadingCert(false);
    }
  };

  const handlePrivateKeyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.key')) {
      toast.error('El archivo debe tener extensión .key');
      return;
    }

    setUploadingKey(true);
    try {
      const response = await uploadFacturamaPrivateKey(file);
      setFormData({ ...formData, facturama_private_key: response.data.private_key_base64 });
      setKeyFileName(file.name);
      toast.success('Llave privada subida exitosamente');
    } catch (error) {
      console.error('Error uploading private key:', error);
      toast.error(getErrorMessage(error, 'Error al subir la llave privada'));
    } finally {
      setUploadingKey(false);
    }
  };

  const clearCertificate = () => {
    setFormData({ ...formData, facturama_certificate: '' });
    setCertFileName('');
  };

  const clearPrivateKey = () => {
    setFormData({ ...formData, facturama_private_key: '' });
    setKeyFileName('');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      toast.error('El logo debe ser una imagen PNG o JPG');
      return;
    }

    // Validate file size (max 2MB for logos)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El logo no debe superar 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.post('/uploads/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const logoUrl = response.data.logo_url;
      setFormData({ ...formData, facturama_logo: logoUrl });
      setLogoFileName(file.name);
      toast.success('Logo cargado exitosamente');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error al cargar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const clearLogo = () => {
    setFormData({ ...formData, facturama_logo: '' });
    setLogoFileName('');
  };

  const handleRegistrarCSD = async () => {
    // Validate that cert/key/password are provided locally
    if (!formData.facturama_certificate || !formData.facturama_private_key || !formData.facturama_private_key_password) {
      toast.error('Sube el certificado (.cer), llave privada (.key) e ingresa la contraseña antes de registrar.');
      return;
    }

    setRegistrandoCSD(true);
    try {
      const diagResponse = await diagnosticarCSD();
      const diag = diagResponse.data;

      if (!diag.ok) {
        const issueText = diag.issues.map(i => `\u2022 ${i}`).join('\n');
        toast.error(`No se puede registrar el CSD:\n${issueText}`, { duration: 15000 });
        return;
      }

      // Send cert/key/password directly to the endpoint (transient, not stored in DB)
      const response = await registrarCSD({
        certificate: formData.facturama_certificate,
        private_key: formData.facturama_private_key,
        private_key_password: formData.facturama_private_key_password,
      });
      toast.success('Certificado digital registrado exitosamente');
      setCsdStatus({ registered: true, ...response.data });

      // Clear sensitive data from local state after successful registration
      setFormData(prev => ({
        ...prev,
        facturama_certificate: '',
        facturama_private_key: '',
        facturama_private_key_password: '',
      }));
      setCertFileName('');
      setKeyFileName('');
    } catch (error) {
      console.error('Error registering CSD:', error);
      toast.error(getErrorMessage(error, 'Error al registrar certificado'), { duration: 15000 });
    } finally {
      setRegistrandoCSD(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Settings className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay negocio configurado</h2>
          <p className="text-slate-500">Primero debes crear o vincular tu negocio para acceder a la configuración.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Configuración</h1>
            <p className="text-slate-600 mt-2">Personaliza tu negocio</p>
          </div>
          {formData.settings.verification_badge && (
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <Award className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Negocio Verificado</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-6 h-6 text-teal-700" />
            Información del Negocio
          </h2>
          <div className="space-y-4">
            <div>
              <ImageUpload
                value={formData.logo_url}
                onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
                onThumbnailChange={(url) => setFormData(prev => ({ ...prev, logo_thumbnail_url: url }))}
                type="logo"
                label="Logotipo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del Negocio *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">RFC</label>
                <input
                  type="text"
                  value={formData.rfc}
                  onChange={(e) => setFormData({ ...formData, rfc: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="XAXX010101000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
                <textarea
                  rows="2"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Calle, número, colonia, ciudad"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Código Postal *
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData({ ...formData, postal_code: value });
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="44100"
                  maxLength={5}
                />
                <p className="text-xs text-slate-500 mt-1">Requerido para facturación</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Latitud
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.lat}
                  onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="19.4326"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Longitud
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.lng}
                  onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="-99.1332"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-700" />
            Métodos de Pago
          </h2>
          <p className="text-sm text-slate-600 mb-4">Selecciona los métodos de pago que aceptas</p>
          <div className="space-y-3">
            {[
              { value: 'cash', label: 'Efectivo' },
              { value: 'card', label: 'Tarjeta (débito/crédito)' },
              { value: 'telcash', label: 'TelCash / Transferencia' },
            ].map((method) => (
              <label
                key={method.value}
                className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.settings.payment_methods.includes(method.value)}
                  onChange={() => togglePaymentMethod(method.value)}
                  className="w-5 h-5 text-teal-700 rounded focus:ring-2 focus:ring-teal-500"
                />
                <span className="font-medium text-slate-900">{method.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Truck className="w-6 h-6 text-orange-600" />
            Opciones de Entrega
          </h2>
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <div>
                <p className="font-medium text-slate-900">Entrega a Domicilio</p>
                <p className="text-sm text-slate-500">Con repartidores propios</p>
              </div>
              <input
                type="checkbox"
                checked={formData.settings.delivery_enabled}
                onChange={(e) => updateSetting('delivery_enabled', e.target.checked)}
                className="w-5 h-5 text-teal-700 rounded focus:ring-2 focus:ring-teal-500"
              />
            </label>

            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <div>
                <p className="font-medium text-slate-900">Recolección en Sucursal</p>
                <p className="text-sm text-slate-500">El cliente recoge en tienda</p>
              </div>
              <input
                type="checkbox"
                checked={formData.settings.pickup_enabled}
                onChange={(e) => updateSetting('pickup_enabled', e.target.checked)}
                className="w-5 h-5 text-teal-700 rounded focus:ring-2 focus:ring-teal-500"
              />
            </label>

            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <div>
                <p className="font-medium text-slate-900">Envío por Paquetería</p>
                <p className="text-sm text-slate-500">FedEx, DHL, Estafeta, etc.</p>
              </div>
              <input
                type="checkbox"
                checked={formData.settings.shipping_enabled}
                onChange={(e) => updateSetting('shipping_enabled', e.target.checked)}
                className="w-5 h-5 text-teal-700 rounded focus:ring-2 focus:ring-teal-500"
              />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-purple-600" />
            Venta de Mayoreo
          </h2>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <div>
                <p className="font-medium text-slate-900">Habilitar Venta de Mayoreo</p>
                <p className="text-sm text-slate-500">Permite precios especiales por volumen</p>
              </div>
              <input
                type="checkbox"
                checked={formData.settings.wholesale_enabled}
                onChange={(e) => updateSetting('wholesale_enabled', e.target.checked)}
                className="w-5 h-5 text-teal-700 rounded focus:ring-2 focus:ring-teal-500"
              />
            </label>

            {formData.settings.wholesale_enabled && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Condiciones Generales para Mayoreo
                </label>
                <textarea
                  rows="4"
                  value={formData.settings.wholesale_conditions}
                  onChange={(e) => updateSetting('wholesale_conditions', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Ejemplo: Compra mínima 12 unidades. Pago anticipado. Envío sin costo en compras mayores a $1000."
                />
              </div>
            )}
          </div>
        </div>

        {formData.settings.billing_enabled && (
          <div className="bg-white rounded-xl border border-green-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-green-600" />
              Configuración de Facturación Electrónica
            </h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800">
                La facturación electrónica está habilitada. Configura tus credenciales del CSD (Certificado de Sello Digital) para emitir facturas.
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">RFC del Emisor *</label>
                  <input
                    type="text"
                    value={formData.facturama_rfc}
                    onChange={(e) => setFormData({ ...formData, facturama_rfc: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono uppercase"
                    placeholder="XAXX010101000"
                    maxLength={13}
                    data-testid="facturama-rfc-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">RFC con el que emitirás las facturas</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Código Postal del Emisor *</label>
                  <input
                    type="text"
                    value={formData.facturama_codigo_postal}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setFormData({ ...formData, facturama_codigo_postal: value });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="44100"
                    maxLength={5}
                  />
                  <p className="text-xs text-slate-500 mt-1">Código postal del domicilio fiscal</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Razón Social del Emisor *</label>
                  <input
                    type="text"
                    value={formData.facturama_razon_social}
                    onChange={(e) => setFormData({ ...formData, facturama_razon_social: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 uppercase"
                    placeholder="EMPRESA EJEMPLO SA DE CV"
                  />
                  <p className="text-xs text-slate-500 mt-1">Exactamente como aparece en tu Constancia de Situación Fiscal</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Régimen Fiscal *</label>
                  <select
                    value={formData.regimen_fiscal}
                    onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="601">601 - General de Ley PM</option>
                    <option value="603">603 - Personas Morales sin Fines de Lucro</option>
                    <option value="605">605 - Sueldos y Salarios</option>
                    <option value="606">606 - Arrendamiento</option>
                    <option value="608">608 - Demás Ingresos</option>
                    <option value="612">612 - Actividades Empresariales y Profesionales</option>
                    <option value="614">614 - Ingresos por Intereses</option>
                    <option value="616">616 - Sin Obligaciones Fiscales</option>
                    <option value="621">621 - Incorporación Fiscal</option>
                    <option value="625">625 - Plataformas Tecnológicas</option>
                    <option value="626">626 - Régimen Simplificado de Confianza</option>
                  </select>
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Logo para Facturas (opcional)
                </label>
                {formData.facturama_logo ? (
                  <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="w-24 h-16 bg-white rounded border border-blue-200 flex items-center justify-center overflow-hidden">
                      <img
                        src={formData.facturama_logo.startsWith('/api/') ? formData.facturama_logo : `data:image/png;base64,${formData.facturama_logo}`}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">
                        {logoFileName || 'Logo cargado'}
                      </p>
                      <p className="text-xs text-blue-600">Este logo aparecerá en el PDF de tus facturas</p>
                    </div>
                    <button
                      type="button"
                      onClick={clearLogo}
                      className="p-1 hover:bg-blue-100 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-blue-700" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex flex-col items-center justify-center py-4">
                      {uploadingLogo ? (
                        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
                      ) : (
                        <>
                          <Image className="w-8 h-8 text-slate-400 mb-2" />
                          <p className="text-sm text-slate-600 font-medium">Haz clic para subir el logo</p>
                          <p className="text-xs text-slate-400">PNG o JPG, máx. 500KB, recomendado 200x100px</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                  </label>
                )}
                <p className="text-xs text-slate-500 mt-1">El logo aparecerá en el PDF de todas tus facturas</p>
              </div>

              {/* Certificate Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Certificado (.cer) *</label>
                {formData.facturama_certificate ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <File className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">
                        {certFileName || 'Certificado cargado'}
                      </p>
                      <p className="text-xs text-green-600">Archivo listo para guardar</p>
                    </div>
                    <button
                      type="button"
                      onClick={clearCertificate}
                      className="p-1 hover:bg-green-100 rounded-full transition-colors"
                      data-testid="clear-certificate-btn"
                    >
                      <X className="w-4 h-4 text-green-700" />
                    </button>
                  </div>
                ) : (
                  <label 
                    className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid="certificate-upload-zone"
                  >
                    <div className="flex flex-col items-center justify-center py-4">
                      {uploadingCert ? (
                        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                          <p className="text-sm text-slate-600 font-medium">Haz clic para subir el certificado</p>
                          <p className="text-xs text-slate-400">Archivo .cer (máx. 1MB)</p>
                        </>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".cer"
                      onChange={handleCertificateUpload}
                      disabled={uploadingCert}
                      data-testid="certificate-file-input"
                    />
                  </label>
                )}
              </div>

              {/* Private Key Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Llave Privada (.key) *</label>
                {formData.facturama_private_key ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <File className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">
                        {keyFileName || 'Llave privada cargada'}
                      </p>
                      <p className="text-xs text-green-600">Archivo listo para guardar</p>
                    </div>
                    <button
                      type="button"
                      onClick={clearPrivateKey}
                      className="p-1 hover:bg-green-100 rounded-full transition-colors"
                      data-testid="clear-private-key-btn"
                    >
                      <X className="w-4 h-4 text-green-700" />
                    </button>
                  </div>
                ) : (
                  <label 
                    className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid="private-key-upload-zone"
                  >
                    <div className="flex flex-col items-center justify-center py-4">
                      {uploadingKey ? (
                        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                          <p className="text-sm text-slate-600 font-medium">Haz clic para subir la llave privada</p>
                          <p className="text-xs text-slate-400">Archivo .key (máx. 1MB)</p>
                        </>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".key"
                      onChange={handlePrivateKeyUpload}
                      disabled={uploadingKey}
                      data-testid="private-key-file-input"
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña de la Llave Privada *</label>
                <input
                  type="password"
                  value={formData.facturama_private_key_password}
                  onChange={(e) => setFormData({ ...formData, facturama_private_key_password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="••••••••"
                  data-testid="facturama-password-input"
                />
                <p className="text-xs text-slate-500 mt-1">Contraseña que usaste al generar el CSD en el SAT</p>
              </div>
              {/* CSD Status & Registration */}
              <div className={`rounded-lg p-4 border ${csdStatus?.registered ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${csdStatus?.registered ? 'bg-green-100' : 'bg-slate-200'}`}>
                    {csdStatus?.registered ? (
                      <Shield className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 text-sm">
                      {csdStatus?.registered ? 'Certificado Digital Activo' : 'Certificado Digital No Registrado'}
                    </h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {csdStatus?.registered
                        ? `RFC: ${csdStatus.rfc} — Listo para emitir facturas`
                        : 'Sube tu certificado (.cer), llave privada (.key) y contraseña para registrar tu CSD en Facturama'
                      }
                    </p>
                    <button
                      type="button"
                      onClick={handleRegistrarCSD}
                      disabled={registrandoCSD || !formData.facturama_certificate || !formData.facturama_private_key || !formData.facturama_private_key_password}
                      className="mt-2 flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="config-registrar-csd-btn"
                    >
                      {registrandoCSD ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Registrando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          {csdStatus?.registered ? 'Re-registrar Certificado Digital' : 'Registrar Certificado Digital'}
                        </>
                      )}
                    </button>
                    {(!formData.facturama_certificate || !formData.facturama_private_key || !formData.facturama_private_key_password) && (
                      <p className="text-xs text-amber-600 mt-1">
                        Sube el certificado, llave privada e ingresa la contraseña para poder registrar.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-xs text-green-800">
                  <strong>Seguridad:</strong> Los archivos del CSD (.cer, .key) y la contraseña NO se almacenan en nuestros servidores.
                  Solo se envían directamente a Facturama al momento de registrar. Puedes obtener tu CSD en <a href="https://www.sat.gob.mx" target="_blank" rel="noopener noreferrer" className="underline">sat.gob.mx</a>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Estado de Funcionalidades</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Plan Actual</p>
                <p className="text-sm text-slate-500 capitalize">{business?.plan_type}</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                {business?.subscription_status === 'active' ? 'Activo' : 'Inactivo'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Insignia de Verificación</p>
                <p className="text-sm text-slate-500">Solo activado por administradores</p>
              </div>
              <span className={`px-3 py-1 ${formData.settings.verification_badge ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'} text-sm font-medium rounded-full`}>
                {formData.settings.verification_badge ? 'Activo' : 'Inactivo'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Facturación Electrónica</p>
                <p className="text-sm text-slate-500">Genera facturas CFDI 4.0</p>
              </div>
              <span className={`px-3 py-1 ${formData.settings.billing_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} text-sm font-medium rounded-full`}>
                {formData.settings.billing_enabled ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings-button"
            className="px-6 py-3 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Configuracion;
